/**
 * SiteBanner Component
 *
 * Displays admin-created banners (from Communications) to authenticated users.
 * Supports priority-based styling and dismissal.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';
import { X, AlertTriangle, Info, AlertCircle, Bell } from 'lucide-react';
import { UnifiedButton } from '@/components/ui/UnifiedButton';
import DOMPurify from 'dompurify';

interface Banner {
  id: string;
  title: string;
  content: string;
  priority: 'critical' | 'high' | 'normal' | 'low';
  placement: string;
  expiresAt: string | null;
  createdAt: string;
}

const priorityStyles: Record<string, { bg: string; border: string; icon: typeof AlertTriangle }> = {
  critical: { bg: 'bg-red-500/15', border: 'border-red-500/40', icon: AlertTriangle },
  high: { bg: 'bg-orange-500/15', border: 'border-orange-500/40', icon: AlertCircle },
  normal: { bg: 'bg-blue-500/15', border: 'border-blue-500/40', icon: Info },
  low: { bg: 'bg-gray-500/15', border: 'border-gray-500/40', icon: Bell },
};

export function SiteBanner() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [dismissedLocal, setDismissedLocal] = useState<Set<string>>(new Set());

  const { data } = useQuery({
    queryKey: ['active-banners'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/communications/active-banners');
      if (!res.ok) return { banners: [] };
      return res.json();
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const dismissMutation = useMutation({
    mutationFn: async (bannerId: string) => {
      await apiRequest('POST', `/api/communications/${bannerId}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-banners'] });
    },
  });

  const banners: Banner[] = data?.banners || [];
  const visibleBanners = banners.filter(b => !dismissedLocal.has(b.id));

  // Update nav offset when banners are shown
  useEffect(() => {
    const bannerHeight = visibleBanners.length > 0 ? visibleBanners.length * 48 : 0;
    const currentOffset = parseInt(
      document.documentElement.style.getPropertyValue('--nav-offset') || '0',
    );
    // Only adjust if impersonation banner isn't already setting it
    if (currentOffset < 48 && bannerHeight > 0) {
      document.documentElement.style.setProperty('--nav-offset', `${bannerHeight}px`);
      return () => {
        document.documentElement.style.setProperty('--nav-offset', '0px');
      };
    }
  }, [visibleBanners.length]);

  if (!isAuthenticated || visibleBanners.length === 0) return null;

  const handleDismiss = (bannerId: string) => {
    setDismissedLocal(prev => new Set([...prev, bannerId]));
    dismissMutation.mutate(bannerId);
  };

  return (
    <>
      {visibleBanners.map(banner => {
        const style = priorityStyles[banner.priority] || priorityStyles.normal;
        const Icon = style.icon;

        return (
          <div
            key={banner.id}
            className={`${style.bg} ${style.border} border-b backdrop-blur-sm px-4 py-2.5 flex items-center justify-between z-50 relative`}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Icon className="w-4 h-4 shrink-0 text-white/80" />
              <span className="text-white font-medium text-sm truncate">{banner.title}</span>
              {banner.content && banner.content !== banner.title && (
                <span
                  className="text-white/70 text-sm hidden sm:inline truncate"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(banner.content) }}
                />
              )}
            </div>
            <UnifiedButton
              variant="ghost"
              size="sm"
              onClick={() => handleDismiss(banner.id)}
              className="text-white/60 hover:text-white ml-2 p-1 shrink-0"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </UnifiedButton>
          </div>
        );
      })}
    </>
  );
}
